// /config.json：vms-web 依 Host 回的品牌設定（rust/src/web.rs）。
// SPA 啟動時先拿這份，再 configureKinio()，所以同一份 build 可服務所有品牌。
import { planeBase } from '@kinio/web-sdk';
import type { KinioFamily } from './kinioFamily';

export type MapProvider = 'google' | 'baidu';

export interface AppConfig {
  brand: string;
  /** portal 建的 VMS App（vms-web 範本）才有（SPEC #vms-web-193）；brand pack（vms.yaml）沒有。 */
  app_id?: string;
  display_name: string;
  /** MyCam（SPEC #mycam-617、#mycam-v1 MV16）：enabled = 品牌開了 MyCam；active = 這個 Host 是 mycam.<brand>…（預設簡易模式）。 */
  mycam?: { enabled: boolean; active: boolean; default_ui_mode: 'simple' | 'full' };
  env: string;
  identity_base: string;
  /** 品牌的 VMS client（只有一個；跟品牌 App 同一個帳號池）。 */
  identity: { client_id?: string };
  /** logo / favicon 是 brand pack 的預設；品牌在 portal 指定了 VMS 圖示時由 withVmsBranding() 換掉。
   *  apple_touch_icon 不在 /config.json 裡，只有品牌指定了圖示才由 SPA 補上（mark-180）。 */
  theme: { accent?: string; logo?: string; favicon?: string; apple_touch_icon?: string };
  maps: {
    google?: { browser_key?: string; map_id?: string };
    baidu?: { ak?: string };
    default_by_region: Record<string, MapProvider>;
    /** dev / test 兩家都沒 key 時的開發底圖（vms.yaml maps.dev_tiles；SPEC #vms-web-290，第二輪 B2-15）。沒有 = 內建的 OpenStreetMap 社群圖磚 */
    dev_tiles?: { url: string; attribution?: string; subdomains?: string; max_zoom?: number };
  };
  features: Record<string, boolean>;
  /** env → plane api（還沒有任何家戶的帳號也要知道「我的設備」與組織 API 在哪）。 */
  planes?: Record<string, string>;
  /** 同品牌 portal 主機：DEV-02 ③ 交給 App 的配對深連結（provisioning §5.1）；空 = 品牌沒有 portal，③ 不提供。 */
  portal_host?: string;
  /** Kinio KMS 簽章網址 https://sign.<plane 網域>/（SPEC #kinio-kms-security、D35）；空 = 這個 plane 沒開。 */
  sign_url?: string;
  /** Kinio KMS 遠端協助的操作端工具下載網址（vms-web 設定 kmgrctl.downloads；SPEC #kinio-kms-remote「操作端」）。沒有的平台寫「還沒有開放下載」。 */
  kmgrctl_downloads?: Partial<Record<'windows' | 'macos' | 'linux', string>>;
  /** Kinio Station / Node 開放的品牌與機種（啟動時從平台讀，SPEC #catalog-kinio-family-brands；lib/kinioFamily.ts）。沒有 = 預設值。 */
  kinio_family?: KinioFamily;
  /** 部署的 artifact id（沒有 artifact id 的本機 / 舊部署是伺服器的建置版本）。 */
  version: string;
  /** 這份 vms-web 的 commit（部署時給了 artifact id 才有；使用者選單「網頁 <commit>」，第二輪 B2-17）。 */
  build?: string;
}

/** dev 與 test 有同一套開發者功能（例：兩家地圖都沒 key 時的開發底圖）；prod 沒有（SPEC #envs）。 */
export function hasDevFeatures(env: string): boolean {
  return env === 'dev' || env === 'test';
}

export async function loadAppConfig(fetchImpl: typeof fetch = fetch): Promise<AppConfig> {
  const res = await fetchImpl('/config.json', { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`config.json ${res.status}`);
  return (await res.json()) as AppConfig;
}

/** 依國家碼選地圖供應商（plan/vms-web-v0.md §5.2.2）；使用者手動選擇優先。 */
export function defaultMapProvider(cfg: AppConfig, countryCode?: string, userChoice?: MapProvider | null): MapProvider {
  if (userChoice) return userChoice;
  const byRegion = cfg.maps.default_by_region ?? {};
  return (countryCode && byRegion[countryCode.toUpperCase()]) || byRegion['*'] || 'google';
}

/** 品牌的 VMS 圖示：公開 API 回的檔案網址（SPEC #catalog-s17-10）。 */
export interface VmsBranding {
  logo: string; // mark-128：標題列、登入頁
  favicon: string; // mark-32
  apple_touch_icon: string; // mark-180
}

export const VMS_BRANDING_TIMEOUT_MS = 2000;

const SHA256 = /^[0-9a-f]{64}$/;

/** 問 planes[env] 的 GET /v1/brands/{brand}/vms-branding（不帶 token、逾時 2 秒）。
 *  有 bundle → 該組 mark 的檔案網址；{"bundle":null}、沒有該 env 的 plane、失敗、逾時、格式不對 → null（用 brand pack）。 */
export async function fetchVmsBranding(
  cfg: AppConfig,
  fetchImpl: typeof fetch = fetch,
  timeoutMs = VMS_BRANDING_TIMEOUT_MS,
): Promise<VmsBranding | null> {
  const api = cfg.planes?.[cfg.env];
  if (!api || !cfg.brand) return null;
  const base = `${planeBase(api)}/v1/brands/${encodeURIComponent(cfg.brand)}/vms-branding`;
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const res = await fetchImpl(base, { headers: { Accept: 'application/json' }, credentials: 'omit', signal: ctl.signal });
    if (!res.ok) return null;
    const body = (await res.json()) as { bundle?: unknown; files?: Record<string, unknown> } | null;
    if (!body || typeof body.bundle !== 'string' || !body.bundle) return null;
    const file = (name: string) => {
      const sha = body.files?.[name];
      return typeof sha === 'string' && SHA256.test(sha) ? `${base}/files/${sha}` : null;
    };
    const logo = file('mark-128.png');
    const favicon = file('mark-32.png');
    const touch = file('mark-180.png');
    if (!logo || !favicon || !touch) return null;
    return { logo, favicon, apple_touch_icon: touch };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** 品牌指定了 VMS 圖示就換掉 brand pack 的 logo / favicon；沒有就原樣。 */
export function withVmsBranding(cfg: AppConfig, b: VmsBranding | null): AppConfig {
  if (!b) return cfg;
  return { ...cfg, theme: { ...cfg.theme, logo: b.logo, favicon: b.favicon, apple_touch_icon: b.apple_touch_icon } };
}

/** 套品牌：強調色、favicon、apple-touch-icon、標題。 */
export function applyBrand(cfg: AppConfig, doc: Document = document): void {
  if (cfg.theme.accent) doc.documentElement.style.setProperty('--accent', cfg.theme.accent);
  if (cfg.theme.favicon) doc.getElementById('favicon')?.setAttribute('href', cfg.theme.favicon);
  if (cfg.theme.apple_touch_icon) {
    let link = doc.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    if (!link) {
      link = doc.createElement('link');
      link.rel = 'apple-touch-icon';
      doc.head.appendChild(link);
    }
    link.href = cfg.theme.apple_touch_icon;
  }
  doc.title = cfg.display_name;
}
